import { useEffect, useMemo, useState } from "react";
import { StyleSheet, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  fetchProgramWarnings,
  validateProgramDraft,
  type MuscleAdjacencyWarning,
  type ProgramDraftWarning,
} from "@heraion/shared";
import { resolveTheme } from "../theme";
import { useThemePreferenceContext } from "../ThemeContext";
import { UnsavedChangesDialog } from "../primitives/UnsavedChangesDialog";
import { Toast } from "../primitives/Toast";
import { EditorSaveStatus } from "../ProgramEditor/EditorSaveStatus";
import { DraftResumeDialog } from "../ProgramEditor/DraftResumeDialog";
import type { StoredEditorDraft } from "../ProgramEditor/useDraftResume";

import { makeComposerStyles } from "./composerStyles";
import { usePlans } from "../Plans/usePlans";
import { usePrograms } from "../Programs/usePrograms";
import { useComposerGuard } from "./useComposerGuard";
import { ProgramEditorV3 } from "../ProgramEditorV3/ProgramEditorV3";
import { ComposerAssigneesBand } from "./ComposerAssigneesBand";
import { ComposerPublishBand } from "./ComposerPublishBand";
import { ComposerWarningsPanel } from "./ComposerWarningsPanel";
import type { ProgramComposerProps } from "./types";

// The program editor's body (#336: split from ProgramComposer.tsx, which
// keeps the pre-mount step: the device draft read, the «new draft» question,
// the start stage and the Plan fork). Mounts ProgramEditorV3, on the phone
// and on the web (slice 6), with the save warnings, the publication band, the
// assignee band, the save line, the toast and the two dialogs.
export function ComposerBody({
  apiFetch,
  durableWrite,
  dark,
  program,
  creationSeed,
  catalog,
  Picker,
  onSaved,
  onCancel,
  registerBackGuard,
  exerciseCatalog,
  unitsPreference,
  profile,
  confirm,
  animationSource,
  onAnimationLoadError,
  callerSub,
  athleteRoster,
  authoredAs,
  keepOpen,
  draftScope,
  draftOwner,
  progress,
  maxMeta,
  stored,
}: ProgramComposerProps & { stored: StoredEditorDraft | null }) {
  const { t } = useTranslation();
  const family = useThemePreferenceContext()?.preference.family ?? "brand";
  const theme = resolveTheme(dark, family);
  const p = theme.palette;
  const cs = useMemo(() => makeComposerStyles(p), [p]);

  // #76: back with an edited draft asks first; an untouched one leaves silently.
  // #336: plus the device draft, the resume question and the save line.
  const { composer, guard, saveState, toast, conflict, savedElsewhere } = useComposerGuard(
    {
      apiFetch,
      durableWrite,
      program,
      creationSeed,
      catalog,
      onSaved,
      mode: "program",
      unitsPreference,
      authoredAs,
      keepOpen,
    },
    {
      onLeave: onCancel,
      registerBackGuard,
      draftScope,
      stored,
      callerSub: draftOwner,
    },
  );
  // `save`/`busy`/`draft` are read by ComposerPublishBand off the composer it
  // receives; `error`/`busy` also ride down to the tree's top bar.
  const { draft, busy, error, cellUnit } = composer;
  // Live warn-only validation (Follow+Publish §4): Save saves ALWAYS. The
  // MAXes are the host's (slice 6: no second read here); until the host has
  // them the %MAX check is skipped (null), never guessed. Hard guards stay
  // in save().
  const maxes = useMemo(
    () => (maxMeta ? new Map([...maxMeta].map(([id, m]) => [id, m.valueKg])) : null),
    [maxMeta],
  );
  const [adjacencyWarnOpen, setAdjacencyWarnOpen] = useState(false);
  const warnings = useMemo(() => validateProgramDraft(draft, maxes), [draft, maxes]);
  const [warnOpen, setWarnOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);

  // PE-21: server-computed muscle-adjacency advisory (packages/api's
  // programWarnings), fetched once at mount alongside maxes: best-effort,
  // never blocks. Existing programs only (`program?.id`): a brand-new
  // create-from-scratch draft has no id to fetch yet.
  const [adjacencyWarnings, setAdjacencyWarnings] = useState<
    readonly MuscleAdjacencyWarning[]
  >([]);
  useEffect(() => {
    const id = program?.id;
    if (!id) {
      setAdjacencyWarnings([]);
      return;
    }
    let cancelled = false;
    fetchProgramWarnings(apiFetch, id)
      .then((w) => {
        if (!cancelled) setAdjacencyWarnings(w);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // Mount-only per program id: hosts pass a module-level apiFetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program?.id]);
  const resolveName = useMemo(() => {
    const bySlug = new Map(catalog.map((c) => [c.slug, c.name]));
    return (id: string, snapshot?: string) => snapshot ?? bySlug.get(id) ?? id;
  }, [catalog]);

  const warningLabel = (w: ProgramDraftWarning): string =>
    w.code === "emptyCell"
      ? t("composer.checkEmptyCell", {
          w: w.week + 1,
          d: w.dayIdx + 1,
          defaultValue: "W{{w}}·D{{d}} has no exercises",
        })
      : t("composer.checkPctNoMax", {
          name: resolveName(w.exerciseId),
          defaultValue: "{{name}} uses %MAX without a saved MAX",
        });

  const warningsPanel = (
    <ComposerWarningsPanel
      theme={theme}
      cs={cs}
      warnings={warnings}
      warningLabel={warningLabel}
      warnOpen={warnOpen}
      setWarnOpen={setWarnOpen}
      adjacencyWarnings={adjacencyWarnings}
      adjacencyWarnOpen={adjacencyWarnOpen}
      setAdjacencyWarnOpen={setAdjacencyWarnOpen}
    />
  );

  // PE-39/K5: the publication lifecycle, assembled here beside the warnings
  // panel and handed to the editor as an element.
  // PE-40: the athlete's saved single workouts, for the picker's Workout tab.
  // ⛔ `durableWrite` is the hook's own write seam and is passed because the
  // hook asks for it, not because this composer writes plans: it does not.
  const plans = usePlans({ apiFetch, durableWrite });
  // #85 (#336): the athlete's own and assigned programs, for the editor's
  // Programs tab. The one being edited is left out: copying its own days is
  // the day menu's job.
  // #336: the height of the editor's selection bar, so the toast
  // (its Undo above all) is not drawn over it.
  const [toastLift, setToastLift] = useState(0);
  const programsList = usePrograms({ apiFetch, durableWrite }).programs;
  const otherPrograms = useMemo(
    () => (programsList ?? []).filter((other) => other.id !== program?.id),
    [programsList, program?.id],
  );

  const publishBand = (
    <ComposerPublishBand
      composer={composer}
      cs={cs}
      theme={theme}
      warnings={warnings}
      warningLabel={warningLabel}
      publishOpen={publishOpen}
      setPublishOpen={setPublishOpen}
    />
  );

  // PE-48: the assignee panel, built here (same recipe as publishBand
  // above) and handed to the editor as an element. Absent host seam
  // (callerSub) → no band, same degrade every other optional seam on this
  // composer uses.
  // CO-20: the gate used to be `callerSub && athleteRoster`, i.e. a
  // presence-of-data check that happened to also gate the Assegna gesture
  // because only a coach mount ever passed a roster. That conflated "is
  // there anyone to add" with "should the band show at all". The gate is
  // now the VIEW: `authoredAs === "coach"` ⇒ Assegna, the gesture CO-20
  // decided. ⛔ The athlete side (Condividi) is BUILT but deliberately NOT
  // mounted: it is PE-51, whose trust model (who may share, and whether the
  // receiver gets a copy or a reference) is Antenore's open decision
  // (next/archive/backlog.md PE-51). Shipping the affordance would settle it by accident.
  // Flip this one condition the day PE-51 is decided; nothing else moves.
  // `athleteRoster` degrades to `[]`: it is data the band handles, not a gate.
  const assigneesBand = callerSub && authoredAs === "coach" ? (
    <ComposerAssigneesBand
      theme={theme}
      apiFetch={apiFetch}
      kind="program"
      authoredAs={authoredAs}
      callerSub={callerSub}
      athleteRoster={athleteRoster ?? []}
      assignedTo={draft.assignedTo}
      assigneeEmails={draft.assigneeEmails}
      onChangeAssignedTo={(next) =>
        composer.setDraft((d) => ({ ...d, assignedTo: next }))
      }
      onChangeAssigneeEmails={(next) =>
        composer.setDraft((d) => ({ ...d, assigneeEmails: next }))
      }
    />
  ) : null;

  // #336 slice 6: THE program editor, on every host and width. ⛔ Plans do NOT come through here:
  // `mode === "single"` is delegated to PlanComposer in ProgramComposer.tsx,
  // before any of this runs.
  // ⛔ Not a fallback UI: the editor reads the host's profile and confirm,
  // and a host that forgets one gets nothing rather than a half-built editor.
  // Every program host passes both (ProgramEditorScreen, PlanScreen,
  // CoachLibraryScreen).
  if (!profile || !confirm) return null;
  // #336: the root View is the toast's positioned parent.
  return (
    <View style={rootStyle.root}>
      <ProgramEditorV3
        theme={theme}
        dark={dark}
        draft={draft}
        setDraft={composer.setDraft}
        cellUnit={cellUnit}
        exerciseCatalog={exerciseCatalog}
        resolveName={resolveName}
        Picker={Picker}
        confirm={confirm}
        structure={composer}
        showToast={toast.show}
        onUndo={composer.undoRedo.onUndo}
        onCancel={guard.handleBack}
        onSave={() => void composer.save()}
        busy={busy}
        error={error ?? null}
        statusLine={<EditorSaveStatus theme={theme} state={saveState} history={composer.undoRedo} />}
        showLiveEditHint={!!program}
        warningsPanel={warningsPanel}
        publishBand={publishBand}
        assigneesBand={assigneesBand}
        workouts={plans.plans}
        programs={otherPrograms}
        onBottomBar={setToastLift}
        profile={profile}
        animationSource={animationSource}
        onAnimationLoadError={onAnimationLoadError}
        progress={progress}
        maxMeta={maxMeta}
        apiFetch={apiFetch}
      />
      <UnsavedChangesDialog theme={theme} {...guard.dialogProps} />
      <DraftResumeDialog
        theme={theme}
        kind="conflict"
        visible={conflict.open}
        onKeep={conflict.onKeepDraft}
        onDrop={conflict.onTakeServer}
      />
      <DraftResumeDialog
        theme={theme}
        kind="savedElsewhere"
        {...savedElsewhere}
        visible={savedElsewhere.visible && !conflict.open}
      />
      <Toast theme={theme} toast={toast.toast} onDismiss={toast.dismiss} bottomInset={toastLift} />
      {toast.region}
    </View>
  );
}

// #336: the toast's positioned parent, the whole editor.
const rootStyle = StyleSheet.create({ root: { flex: 1 } });
